import LinearProgressStyled from 'components/@extended/LinearProgress.tsx';
import Loader from 'components/Loader.tsx';
import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap.tsx';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { canUploadBankTransfers } from 'components/tables/list-table/filter-pieces/filterConstants.ts';
import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil.ts';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes.tsx';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters.tsx';
import { GroupBySelector } from 'components/GroupBySelector';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { EmployeeNameLink } from 'components/record-chip/RecordLink';
import { CheckIcon } from 'components/icons';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
    useGetBankTransfersTotalsQuery,
    useLazyGetBankTransferReceiverIbanOptionsQuery,
    useLazyGetBankTransferReceiverNameOptionsQuery,
    useLazyGetBankTransferSenderIdOptionsQuery,
    useLazyGetBankTransferSenderNameOptionsQuery,
    useLazyGetBankTransfersWithFiltersQuery,
} from 'store/api/bank-transfers.ts';
import { useLazyGetAllEmployeesListWithFiltersQuery } from 'store/api/employees.ts';
import { BankTransactionListItem, BankTransactionTotalsDto } from 'types/bank-transfers.ts';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations.ts';
import { useClassifiers } from 'utils/useClassifiers.ts';
import { useViewParam } from 'hooks/useViewParam';
import { VIEW_PARAM_KEY, VIEW_PRESERVED_PARAM_KEYS } from 'utils/viewParam';
import BankTransferNothingToShow from './BankTransferNothingToShow.tsx';
import BankTransfersTotals from './BankTransfersTotals.tsx';
import BankTransferStatusIcon from './BankTransferStatusIcon.tsx';
import { BankTransferGroupRibbon } from './list/BankTransferGroupRibbon.tsx';
import {
    BANK_TRANSFER_GROUP_BY_OPTIONS,
    BankTransferGroupBy,
    bankTransferGroupKeyOf,
    composeBankTransferSort,
    DEFAULT_BANK_TRANSFER_GROUP_BY,
    formatGroupDate,
    groupBankTransfers,
} from './list/bankTransferGroupBy.ts';

const COLUMN_CONFIG_ID = 'bank-transfers';

// `entity-link` hands the cell's link colour and wrapping to the table's own rule.
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit whitespace-nowrap no-underline hover:underline';

const GROUP_BY_LABEL: Record<BankTransferGroupBy, string> = {
    date: 'bank-transfers.group-by-date',
    uploadedAt: 'bank-transfers.group-by-uploaded-at',
    none: 'bank-transfers.group-by-none',
};

/** What the group ribbon says the group is keyed on, and which row field carries that date. */
const GROUP_CAPTION: Record<Exclude<BankTransferGroupBy, 'none'>, string> = {
    date: 'bank-transfers.date',
    uploadedAt: 'bank-transfers.uploaded-at',
};

const getBankTransferHref = (transfer: BankTransactionListItem) => `/bank-transfers/${transfer.id}`;

interface Props {
    /** A contact's tab: only the transfers paying an invoice this party is the payer of. */
    partyId?: string;
}

const BankTransfersTable = ({ partyId }: Props) => {
    const listId = partyId ? `${COLUMN_CONFIG_ID}-contact` : COLUMN_CONFIG_ID;
    const [fetchData, { data, isFetching }] = useLazyGetBankTransfersWithFiltersQuery();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['CURRENCY', 'BANK_TRANSACTION_STATUS_TYPE'],
    });

    const [searchParams] = useSearchParams();

    const [groupBy, setGroupBy] = useViewParam(listId, BANK_TRANSFER_GROUP_BY_OPTIONS, DEFAULT_BANK_TRANSFER_GROUP_BY);

    // The grouping decides the PRIMARY sort, so groups arrive contiguous instead of being cut up by
    // the 100-row page boundary; the user's column sort then orders rows within each group. A new
    // identity here also refetches from page 0, which is what a changed grouping needs.
    const composeSort = useCallback((columnSort: string) => composeBankTransferSort(groupBy, columnSort), [groupBy]);

    // Its group's ribbon for EVERY row, keyed by row id: the same node is rendered in flow above the
    // row that opens the group and frozen under the header for whichever row the reader has scrolled
    // to, so the group on show stays named while its rows do.
    const { ribbonByRowId, groupOpenerIds } = useMemo(() => {
        const byRowId = new Map<number, JSX.Element>();
        const openerIds = new Set<number>();
        if (groupBy === 'none') {
            return { ribbonByRowId: byRowId, groupOpenerIds: openerIds };
        }
        const keyOf = bankTransferGroupKeyOf(groupBy);
        groupBankTransfers(data?.content ?? [], keyOf).forEach((group, key) => {
            const first = group.transfers[0];
            if (!first) return;
            const ribbon = (
                <BankTransferGroupRibbon
                    caption={t(GROUP_CAPTION[groupBy])}
                    title={key ? formatGroupDate(key) : ''}
                    group={group}
                />
            );
            openerIds.add(first.id);
            group.transfers.forEach((transfer) => byRowId.set(transfer.id, ribbon));
        });
        return { ribbonByRowId: byRowId, groupOpenerIds: openerIds };
    }, [groupBy, data?.content]);

    const renderRowLeadIn = useCallback(
        (transfer: BankTransactionListItem) =>
            (groupOpenerIds.has(transfer.id) ? ribbonByRowId.get(transfer.id) : null) ?? null,
        [ribbonByRowId, groupOpenerIds]
    );

    const renderStickyLeadIn = useCallback(
        (transfer: BankTransactionListItem) => ribbonByRowId.get(transfer.id) ?? null,
        [ribbonByRowId]
    );

    const columns = useMemo<DataTableColumnDef<BankTransactionListItem>[]>(
        () => [
            {
                header: t('bank-transfers.date'),
                accessorKey: 'entryBookingDate',
                meta: {
                    // Content-locked: a nowrap date, so freeze it at the date + the cell's 32px inset
                    // rather than letting it take a share of the flex pool.
                    fixedPx: 124,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && <DateDefaultNowrap dateValue={value} />;
                },
            },
            {
                header: t('bank-transfers.uploaded-at'),
                accessorKey: 'createdAt',
                meta: {
                    // Weighted, never `fixedPx`: that implies lockVisible, and a locked column is
                    // dropped from the picker menu AND filtered out of the hidden set, so it cannot
                    // be hidden at all. The floor keeps the nowrap date from clipping.
                    widthWeight: 0.4,
                    minPx: 124,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && <DateDefaultNowrap dateValue={value} />;
                },
            },
            {
                header: t('bank-transfers.uploaded-by'),
                accessorKey: 'createdBy',
                meta: {
                    widthWeight: 1,
                    minPx: 150,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value?.id ? (
                        <EmployeeNameLink className={LINK_CLASS} to={`/employee/${value.id}`} entity={value} />
                    ) : (
                        value?.name || ''
                    );
                },
            },
            {
                header: t('bank-transfers.status'),
                // Display columns need an explicit id: without one the managed layout would key
                // this column's geometry on its TRANSLATED header.
                id: 'status',
                enableSorting: false,
                meta: {
                    // Weighted rather than fixed, so the column keeps its resize handle: the engine
                    // withholds one from any fixedPx column, that width being the column's own.
                    widthWeight: 0.4,
                    minPx: 80,
                },
                cell: ({ cell }) => {
                    return (
                        <BankTransferStatusIcon
                            bankTransactionStatusTypeCode={cell.row.original.bankTransactionStatusTypeCode}
                        />
                    );
                },
            },
            {
                header: t('bank-transfers.sender-name'),
                id: 'senderName',
                // The accessor is what makes the column sortable — v8 gates getCanSort on one, and
                // manualSorting does not bypass it. The value is unused; the cell renders the link.
                accessorKey: 'entryDebtorName',
                meta: {
                    widthWeight: 1,
                    // Nowrap link: a managed cell clips instead of growing the column to fit its
                    // content, so keep a floor that holds a typical sender name.
                    minPx: 200,
                    sortAccessor: 'entryDebtorName',
                },
                cell: ({ cell }) => {
                    const { id, entryDebtorName } = cell.row.original;
                    return (
                        <RouterLink className={cn(LINK_CLASS, 'font-semibold')} to={`/bank-transfers/${id}`}>
                            {entryDebtorName}
                        </RouterLink>
                    );
                },
            },
            {
                header: t('bank-transfers.description'),
                id: 'description',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: ({ cell }) => {
                    return <>{cell.row.original.entryDescription}</>;
                },
            },
            {
                header: t('bank-transfers.reference-number'),
                id: 'referenceNumber',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    defaultHidden: true,
                },
                cell: ({ cell }) => {
                    return <>{cell.row.original.entryReferenceNumber || '-'}</>;
                },
            },
            {
                header: t('bank-transfers.non-invoice'),
                id: 'nonInvoice',
                enableSorting: false,
                meta: {
                    widthWeight: 0.5,
                    minPx: 110,
                },
                // Centred in the cell rather than through `meta.textAlign`, which is one slot for
                // the header, body and totals cells alike — the header stays left-aligned with
                // every other column's.
                cell: ({ cell }) =>
                    cell.row.original.isNonInvoiceTransaction ? (
                        <div className="flex w-full justify-center">
                            <CheckIcon
                                className="text-primary size-4"
                                // Heavier stroke: a hairline check reads as an empty cell at a glance.
                                strokeWidth={2}
                                role="img"
                                aria-label={t('bank-transfers.not-related-to-invoice')}
                                // Glyphs default to aria-hidden unless given a `titleAccess`, which would also
                                // render a native <title> tooltip over the cell.
                                aria-hidden={false}
                            />
                        </div>
                    ) : null,
            },
            {
                header: t('bank-transfers.processed-outside'),
                id: 'processedOutside',
                enableSorting: false,
                meta: {
                    widthWeight: 0.7,
                    minPx: 150,
                },
                cell: ({ cell }) => {
                    const { amountNoninvoice, entryAmountCurrencyTypeCode } = cell.row.original;
                    // Zero is "nothing was processed outside", not an amount — the upload seeds the
                    // column with 0, so testing for null alone would print 0.00 on every row.
                    return Number(amountNoninvoice) ? (
                        <MoneyDisplay amount={amountNoninvoice!} currency={entryAmountCurrencyTypeCode} />
                    ) : null;
                },
            },
            {
                header: t('bank-transfers.amount'),
                id: 'amount',
                enableSorting: false,
                meta: {
                    // Weighted above the flexible text columns: it carries three label/value lines and the
                    // coverage bar, where they carry one line of text.
                    widthWeight: 1.64,
                    // Three label/value lines plus the coverage bar; below this they collide.
                    minPx: 260,
                },
                cell: ({ cell }) => {
                    const original = cell.row.original;
                    const {
                        entryAmount,
                        entryAmountCurrencyTypeCode,
                        isNonInvoiceTransaction,
                        remainingAmount,
                        coveredRatio,
                        coveredPercent,
                    } = original;
                    return (
                        <div className="flex w-full flex-col">
                            <div className="flex justify-between">
                                <p className="text-body1">
                                    <FormattedMessage id="bank-transfers.transfer-amount" />
                                </p>
                                <div className="flex gap-1">
                                    <span>{entryAmount}</span>
                                    <span>{entryAmountCurrencyTypeCode}</span>
                                </div>
                            </div>
                            {isNonInvoiceTransaction ? (
                                <p className="text-body1">
                                    <FormattedMessage id="bank-transfers.non-invoice-transfer" />
                                </p>
                            ) : (
                                <div className="flex justify-between">
                                    <p className="text-body1">{t('bank-transfers.remaining')}</p>
                                    <div className="flex gap-1">
                                        <span>{remainingAmount}</span>
                                        <span>{entryAmountCurrencyTypeCode}</span>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center gap-1">
                                <p className="text-body1">
                                    <FormattedMessage id="bank-transfers.covered" />
                                </p>
                                <LinearProgressStyled
                                    className="w-full bg-transparent"
                                    variant="determinate"
                                    color={coveredRatio >= 1 ? 'success' : 'secondary'}
                                    value={coveredPercent > 100 ? 100 : coveredPercent}
                                />
                                <div className="flex gap-1">
                                    <p className="text-body1">{coveredPercent}</p>
                                    <p className="text-body1">%</p>
                                </div>
                            </div>
                        </div>
                    );
                },
            },
        ],
        []
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            passiveFilter: partyId ? `invoicePayments.invoice.clientParty.id==${partyId}` : undefined,
            quickFilters: [
                {
                    selector: 'remainingAmount',
                    comperator: '=gt=',
                    filters: [
                        {
                            label: t('bank-transfers.show-only-unmatched'),
                            argument: '0',
                        },
                    ],
                },
            ],
            filters: [
                {
                    type: 'text',
                    label: t('bank-transfers.bank-statement-number'),
                    group: 'bank-transfers.details',
                    selector: 'bankStatementNumber',
                },
                {
                    type: 'daterange',
                    label: t('bank-transfers.date'),
                    shouldDisplayQuickPick: true,
                    group: 'bank-transfers.details',
                    selector: 'entryBookingDate',
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('bank-transfers.sender-name'),
                    group: 'bank-transfers.details',
                    selector: 'entryDebtorName',
                    paginated: {
                        fetch: useLazyGetBankTransferSenderNameOptionsQuery,
                        searchField: 'entryDebtorName',
                        getOptionIdentifier: 'entryDebtorName',
                        getOptionLabel: 'entryDebtorName',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('bank-transfers.sender-id'),
                    group: 'bank-transfers.details',
                    selector: 'entryDebtorId',
                    paginated: {
                        fetch: useLazyGetBankTransferSenderIdOptionsQuery,
                        searchField: 'entryDebtorId',
                        getOptionIdentifier: 'entryDebtorId',
                        getOptionLabel: 'entryDebtorId',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('bank-transfers.receiver-iban'),
                    group: 'bank-transfers.details',
                    selector: 'accountIban',
                    paginated: {
                        fetch: useLazyGetBankTransferReceiverIbanOptionsQuery,
                        searchField: 'accountIban',
                        getOptionIdentifier: 'accountIban',
                        getOptionLabel: 'accountIban',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('bank-transfers.receiver-name'),
                    group: 'bank-transfers.details',
                    selector: 'accountOwnerName',
                    paginated: {
                        fetch: useLazyGetBankTransferReceiverNameOptionsQuery,
                        searchField: 'accountOwnerName',
                        getOptionIdentifier: 'accountOwnerName',
                        getOptionLabel: 'accountOwnerName',
                    },
                },
                {
                    type: 'text',
                    label: t('bank-transfers.reference-number'),
                    group: 'bank-transfers.details',
                    selector: 'entryReferenceNumber',
                },
                {
                    type: 'multiselect',
                    label: t('bank-transfers.currency'),
                    group: 'bank-transfers.details',
                    selector: 'entryAmountCurrencyTypeCode',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['CURRENCY']) : [],
                },
                {
                    type: 'interval',
                    label: t('bank-transfers.amount'),
                    group: 'bank-transfers.details',
                    selector: 'entryAmount',
                    minLabel: t('bank-transfers.amount-min'),
                    maxLabel: t('bank-transfers.amount-max'),
                },
                {
                    type: 'interval',
                    label: t('bank-transfers.processed-outside'),
                    group: 'bank-transfers.details',
                    selector: 'amountNoninvoice',
                    minLabel: t('bank-transfers.processed-outside-min'),
                    maxLabel: t('bank-transfers.processed-outside-max'),
                },
                {
                    type: 'text',
                    label: t('bank-transfers.non-invoice-notes'),
                    group: 'bank-transfers.details',
                    selector: 'noninvoiceNotes',
                },
                {
                    type: 'offset-date-time-range',
                    label: t('bank-transfers.uploaded-at'),
                    shouldDisplayQuickPick: true,
                    group: 'bank-transfers.details',
                    selector: 'createdAt',
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('bank-transfers.uploaded-by'),
                    selector: 'createdBy',
                    comperator: '=cognito=',
                    group: 'bank-transfers.details',
                    paginated: {
                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                        filters: canUploadBankTransfers,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'offset-date-time-range',
                    label: t('bank-transfers.modified-at'),
                    shouldDisplayQuickPick: true,
                    group: 'bank-transfers.details',
                    selector: 'lastModifiedAt',
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('bank-transfers.modified-by'),
                    selector: 'lastModifiedBy',
                    comperator: '=cognito=',
                    group: 'bank-transfers.details',
                    paginated: {
                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                        filters: canUploadBankTransfers,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'checkboxgroup',
                    group: 'bank-transfers.details',
                    selector: 'isNonInvoiceTransaction',
                    filters: [
                        {
                            label: t('bank-transfers.not-related-to-invoice'),
                            argument: 'true',
                        },
                    ],
                },
            ],
            searchBarSelectors: [
                { selector: 'entryDebtorName' },
                { selector: 'entryDescription' },
                { selector: 'entryReferenceNumber' },
            ],
        }),
        [classifiers, partyId]
    );

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    // The tenant-empty state, as opposed to a filter that matched nothing — so it asks whether the
    // user narrowed anything, not whether the URL carries params at all. The grouping control puts
    // its own `view` in the address on mount, and counting that as a filter would retire this state.
    const hasUserParams = [...searchParams.keys()].some((key) => key !== VIEW_PARAM_KEY);
    if (!partyId && data?.empty && !hasUserParams) {
        return <BankTransferNothingToShow />;
    }

    const list = (
        <ListTableWithFilters<BankTransactionListItem>
            getRowHref={getBankTransferHref}
            data={data}
            isLoading={isFetching}
            fetchData={fetchData}
            composeSort={composeSort}
            fetchTotals={useGetBankTransfersTotalsQuery}
            renderTotals={(totals: BankTransactionTotalsDto, isLoadingTotals) => (
                <BankTransfersTotals {...totals} isLoading={isLoadingTotals} />
            )}
            forceTotalsVisible
            rowsPerPage={100}
            filterConfig={filterConfig}
            columns={columns}
            columnConfigId={listId}
            preservedParamKeys={VIEW_PRESERVED_PARAM_KEYS}
            enableColumnPicker
            enableColumnReorder
            enableColumnResize
            renderRowLeadIn={renderRowLeadIn}
            renderStickyLeadIn={renderStickyLeadIn}
            scrollRestorationId={listId}
            canRememberFilters={!partyId}
            quickFilterTrailing={
                <GroupBySelector
                    label={t('bank-transfers.group-by')}
                    value={groupBy}
                    options={BANK_TRANSFER_GROUP_BY_OPTIONS}
                    optionLabel={(option) => t(GROUP_BY_LABEL[option])}
                    onChange={setGroupBy}
                />
            }
        />
    );

    // A tab's panel is no column of its own: the new design spaces the filter row from the table as the list page
    // does.
    return partyId ? <div className="new:flex new:flex-col new:gap-4">{list}</div> : list;
};

export default BankTransfersTable;
